import { isAllocationAllowed } from "@/utilities/promotions";
import { markAsUsed } from "@/utilities/promotions/markAsUsed";
import type { TokenAllocation } from "hooks/firestore/models/tokenAllocation";
import type { TokenAutomation } from "hooks/firestore/models/tokenAutomation";

import type { BetSelection } from "@/features/betslip/types";
import {
  type TokenContextState,
  buildSelectionContext,
  buildUsagesContext,
  buildUsedAllocations,
} from "@/features/betslip/utils/tokenContext";

type AllocationWithId = TokenAllocation & {
  id: string;
  usedAtOutcomeId?: string | null;
  usedAtEventId?: string | null;
};

type Params = {
  slipState: TokenContextState;
  targetSelections: BetSelection[];
  isMulti: boolean;
  allocations: AllocationWithId[];
  automations: TokenAutomation[];
};

/**
 * Run the eligibility pipeline (`isAllocationAllowed` + `markAsUsed`)
 * for a specific bet target (single, SEM, or multi). Returns the
 * stamped allocations the caller can pick from. Used for display in
 * `useTokenAllocations`.
 */
export const filterAllocationsForTarget = ({
  slipState,
  targetSelections,
  isMulti,
  allocations,
  automations,
}: Params): AllocationWithId[] => {
  const selection = buildSelectionContext(slipState, targetSelections, isMulti);
  const usages = buildUsagesContext(
    slipState,
    allocations,
    automations,
    selection,
  );
  const usedAllocations = buildUsedAllocations(slipState);

  const allowed = allocations.filter((allocation) => {
    const automation = automations.find(
      (a) => a.automationId === allocation.automationId,
    );
    if (!automation) return false;
    return isAllocationAllowed({
      allocation,
      automation,
      selection,
      usages,
      usedAllocations,
      allocations,
    });
  });

  return allowed.map((allocation) => {
    const automation = automations.find(
      (a) => a.automationId === allocation.automationId,
    )!;
    return markAsUsed({
      allocation,
      automation,
      selection,
      usages,
      usedAllocations,
      allocations,
    });
  });
};
